import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SoilTypesTable from './SoilTypesTable';
import SoilTypesEdit from './SoilTypesEdit';
import { BOOTSTRAP_BREAKPOINTS, checkBreakpoint } from '@awp/helpers/UIHelper';
import { debounce } from '@awp/helpers/Utils';
import { AwpContainer } from '../Common/AwpContainer';
import { FORM_MODE, Permissions } from '@awp/constants';
import Authorize from '@awp/components/Authorize';
import {
  deleteSoilType,
  addSoilType,
  fetchSoilTypes,
  updateSoilType,
  selectAllSoilTypes,
} from '@awp/stores/soilTypesSlice';
import { toast } from 'react-toastify';

const SoilTypesAdmin = () => {
  const dispatch = useDispatch();
  const { soilTypes, soilTypesLoading } = useSelector(selectAllSoilTypes);
  const [mode, setMode] = useState(FORM_MODE.LIST);
  const [isMedium, setIsMedium] = useState(checkBreakpoint(BOOTSTRAP_BREAKPOINTS.MEDIUM));
  const [isLarge, setIsLarge] = useState(checkBreakpoint(BOOTSTRAP_BREAKPOINTS.LARGE));
  const [data, setData] = useState({});

  useEffect(() => {
    const handleResize = debounce(() => {
      setIsMedium(checkBreakpoint(BOOTSTRAP_BREAKPOINTS.MEDIUM));
      setIsLarge(checkBreakpoint(BOOTSTRAP_BREAKPOINTS.LARGE));
    }, 100);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDelete = (soilType) => {
    dispatch(deleteSoilType(soilType.Id))
      .unwrap()
      .then(() => {
        toast.success(`Soil Type ${soilType.Code} deleted successfully`, { autoClose: 5000 });
        dispatch(fetchSoilTypes());
      })
      .catch((err) => {
        console.error(`Failed to delete: ${err}`);
        toast.error(`Cannot delete soil type ${soilType.Code} because it is used in placement soil layers`, {
          autoClose: 5000,
        });
      });
  };

  const handleAdd = (soilType) => {
    return dispatch(addSoilType(soilType))
      .unwrap()
      .then((result) => {
        if (result) {
          toast.success(`Soil Type ${soilType.code} created successfully`, { autoClose: 5000 });
          return result;
        }
      })
      .catch((err) => {
        console.error(`Failed to create: ${err}`);
        throw err;
      });
  };

  const handleUpdate = (soilType) => {
    return dispatch(updateSoilType(soilType))
      .unwrap()
      .then((result) => {
        if (result) {
          toast.success(`Soil Type ${soilType.code} updated successfully`, { autoClose: 5000 });
          return result;
        }
      })
      .catch((err) => {
        console.error(`Failed to update: ${err}`);
        throw err;
      });
  };

  const tableProps = {
    isMedium,
    isLarge,
    setMode,
    mode,
    setData,
    soilTypes,
    soilTypesLoading,
    onDelete: handleDelete,
  };

  const editProps = {
    setMode,
    mode,
    data,
    setData,
    onAdd: handleAdd,
    onUpdate: handleUpdate,
  };

  return (
    <AwpContainer>
      {mode === FORM_MODE.LIST ? (
        <Authorize requires={[Permissions.ReadInventory]}>
          <SoilTypesTable {...tableProps} />
        </Authorize>
      ) : (
        <Authorize requires={[Permissions.ReadInventory, Permissions.WriteSoilType]}>
          <SoilTypesEdit {...editProps} />
        </Authorize>
      )}
    </AwpContainer>
  );
};

export default SoilTypesAdmin;
